import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import ReverseSyncStatusPanel from "../../components/legacy/ReverseSyncStatusPanel";
import DeliveryAddressPickerButton from "../../components/legacy/DeliveryAddressPickerModal";
import SourceFilesMenu from "../../components/common/SourceFilesMenu";
import salesOrdersService from "../../services/legacy/salesOrdersService";
import salesLineActionsService from "../../services/legacy/salesOrderLinesService";
import customersService from "../../services/legacy/customersService";
import salesOrderInboundMessagesService from "../../services/legacy/salesOrderInboundMessagesService";
import cmtSeleccionBobinasService from "../../services/legacy/cmtSeleccionBobinasService";
import SalesOrderDocumentPageGen, { resolveLineGridRowStyle } from "./generated/SalesOrderDocumentPageGen";
import SalesLineMaterialAlternativesCell from "./salesOrderLines/SalesLineMaterialAlternativesCell";
import SalesLineItemSearchModal from "./salesOrderLines/SalesLineItemSearchModal";
import { estadoPvBadgeStyle, toNumberOrNull, NOT_SYNCED_BADGE_STYLE, NOT_SYNCED_LABEL } from "./seleccionBobinas/seleccionBobinasUtils";

const SELECCION_BOBINAS_ESTADO_LABEL_KEYS = {
  0: "axEnums.CMTSeleccionBobinasEstadoPV.0",
  1: "axEnums.CMTSeleccionBobinasEstadoPV.1",
  2: "axEnums.CMTSeleccionBobinasEstadoPV.2",
  3: "axEnums.CMTSeleccionBobinasEstadoPV.3",
};

const DIRECTION_REPORT_COOLDOWN_MS = 60000;
const CONFIRM_REPORT_COOLDOWN_MS = 60000;
const AX_REFRESH_DEBOUNCE_MS = 1800;
const AX_REFRESH_MAX_WAIT_MS = 5000;
const AX_APPLIED_DUPLICATE_WINDOW_MS = 4000;
const AX_SALESLINE_GRID_FIELDS = new Set([
  "itemId",
  "__materialAlternatives",
  "typeitemid",
  "gramajeitemid",
  "venditemid",
  "salesqtyaux",
  "salesunitaux",
  "salesQty",
  "salesunit",
  "custprice",
  "salesPrice",
  "linedisc",
  "linepercent",
  "custlineamount",
  "lineamount",
  "name",
  "width",
  "depth",
  "mfoid",
  "mfoselected",
]);

const AX_SALESTABLE_GRID_FIELDS = new Set([
  "confirmed",
  "cmtsalespackingstatus",
  "salesid",
  "purchorderformnum",
  "custaccount",
  "invoiceaccount",
  "salestype",
  "salesstatus",
  "currencycode",
  "isalesidblanket",
  "admiteempalmes",
  "customerdeposito",
  "manufacturedorderid",
  "createdby",
  "__opendocument",
]);

const AX_SALESLINE_GRID_ORDER = [
  "itemId",
  "__materialAlternatives",
  "typeitemid",
  "venditemid",
  "gramajeitemid",
  "width",
  "depth",
  "salesqtyaux",
  "salesunitaux",
  "salesQty",
  "salesunit",
  "custprice",
  "salesPrice",
  "linedisc",
  "linepercent",
  "custlineamount",
  "lineamount",
  "name",
  "mfoid",
  "mfoselected",
];

const AX_SALESLINE_GRID_ORDER_INDEX = AX_SALESLINE_GRID_ORDER.reduce((acc, key, index) => {
  acc[key] = index;
  return acc;
}, {});
const AX_SALESLINE_GRID_WIDTHS = {
  typeitemid: { gridWidth: 120, gridMinWidth: 100, gridMaxWidth: 130 },
  name: { gridMinWidth: 260, gridWidth: 320 },
};

// Stable reference so it doesn't retrigger ReverseSyncStatusPanel's
// subscription effect on every render (a fresh array literal would).
const DIRECTION_REPORT_STREAM_ENTITIES = ["SalesTableDirectionReport", "SalesTableConfirmReport"];

const REQUESTED_DELIV_DATE_FIELD = "requesteddelivdate";
const CUSTOMER_ACCOUNT_FIELD = "custaccount";
const PENDING_ACTION_STATUSES = new Set(["pending_manual_review", "ready_to_execute"]);

const MATERIAL_ALTERNATIVE_ROW_DECORATION = {
  style: { backgroundColor: "#dcfce7" },
  title: "Hay otras posibilidades con distinto material para esta línea.",
};

const normalizeBlockedValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  const asString = String(value).trim().toLowerCase();
  if (!asString || asString === "0" || asString === "no" || asString === "false") {
    return 0;
  }
  const asNumber = Number(asString);
  if (Number.isFinite(asNumber)) {
    return asNumber;
  }
  return 1;
};

const isCustomerBlockedForSalesOrder = (blockedValue) =>
  normalizeBlockedValue(blockedValue) > 0;

const getFieldValueLoose = (source, fieldName) => {
  if (!source || typeof source !== "object" || !fieldName) {
    return null;
  }

  const normalizedTarget = fieldName.toLowerCase();
  for (const [key, value] of Object.entries(source)) {
    if (!key || key.toLowerCase() !== normalizedTarget) {
      continue;
    }
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return null;
};

const hasMaterialAlternatives = (row) => Number(getFieldValueLoose(row, "materialAlternativeCount") || 0) > 0;

const isPlaceholderItemId = (itemId) => String(itemId || "").trim().toUpperCase().endsWith("XXXX");

// Numeric-aware equality for salesQty/salesqtyaux comparisons (see
// lineOnFormDataChange): the same quantity can arrive as "10", "10.00" or the
// number 10 depending on whether it came from AX (row data) or from typing in
// the number input, so a plain string comparison would wrongly treat those as
// different values (blocking the mutual-exclusion unlock).
const isSameQtyValue = (a, b) => {
  const normalizedA = a === null || a === undefined || a === "" ? "" : String(a);
  const normalizedB = b === null || b === undefined || b === "" ? "" : String(b);
  if (normalizedA === normalizedB) {
    return true;
  }
  const numericA = normalizedA === "" ? NaN : Number(normalizedA);
  const numericB = normalizedB === "" ? NaN : Number(normalizedB);
  if (Number.isNaN(numericA) || Number.isNaN(numericB)) {
    return false;
  }
  return numericA === numericB;
};

const resolveCreatedSalesOrderIdentity = (response, payload) => {
  const responseIdObj =
    response?.id && typeof response.id === "object" && !Array.isArray(response.id)
      ? response.id
      : null;

  const iSalesId =
    getFieldValueLoose(response, "iSalesId") ||
    getFieldValueLoose(responseIdObj, "iSalesId") ||
    getFieldValueLoose(payload, "iSalesId");

  const dataAreaId =
    getFieldValueLoose(response, "dataAreaId") ||
    getFieldValueLoose(responseIdObj, "dataAreaId") ||
    getFieldValueLoose(payload, "dataAreaId");

  if (!iSalesId) {
    return null;
  }

  return { iSalesId: String(iSalesId), dataAreaId: dataAreaId ? String(dataAreaId) : "" };
};

const resolveSalesOrderIdentityFromRow = (row) => {
  const rowIdObj =
    row?.id && typeof row.id === "object" && !Array.isArray(row.id)
      ? row.id
      : null;

  const iSalesId =
    getFieldValueLoose(row, "iSalesId") ||
    getFieldValueLoose(rowIdObj, "iSalesId");
  const dataAreaId =
    getFieldValueLoose(row, "dataAreaId") ||
    getFieldValueLoose(rowIdObj, "dataAreaId");

  if (!iSalesId) {
    return null;
  }

  return { iSalesId: String(iSalesId), dataAreaId: dataAreaId ? String(dataAreaId) : "" };
};

const buildSalesOrderDetailRoute = ({ iSalesId, dataAreaId }) => {
  const encodedOrderId = encodeURIComponent(iSalesId);
  if (dataAreaId) {
    return `/legacy/sales-orders/${encodeURIComponent(dataAreaId)}/${encodedOrderId}`;
  }
  return `/legacy/sales-orders/document/${encodedOrderId}`;
};

const transformDocumentLineColumns = (baseColumns, onLinesChanged, lineQtyDrivingFieldRef) => {
  const resolveLineGridWidth = (key) =>
    AX_SALESLINE_GRID_WIDTHS[String(key || "").toLowerCase()] || null;

  // salesQty and the aux pair (salesqtyaux/salesunitaux) are mutually
  // exclusive (see lineOnFormOpen/lineOnFormDataChange): whichever side the
  // user touches first becomes the "driver" for this edit session and locks
  // salesQty or salesqtyaux respectively (the locked one gets cleared via
  // formDataPatch there). salesunitaux itself is never locked - it stays
  // pickable even while salesQty is the driver. Driven purely off the ref,
  // not off comparing the other side's live value to its snapshot - the
  // latter would deadlock, since the cleared field's value (now null) also
  // differs from ITS OWN snapshot.
  const resolveQtyExclusionDisabled = (key) => {
    const normalizedKey = String(key || "").toLowerCase();
    if (normalizedKey === "salesqty") {
      return () => lineQtyDrivingFieldRef?.current === "aux";
    }
    if (normalizedKey === "salesqtyaux") {
      return () => lineQtyDrivingFieldRef?.current === "qty";
    }
    return null;
  };

  const materialAlternativesColumn = {
    key: "__materialAlternatives",
    label: "salesLines.materialAlternatives.column",
    sortable: false,
    showInGrid: true,
    showInForm: false,
    showInFilter: false,
    editable: false,
    type: "text",
    gridMinWidth: 360,
    gridWidth: 820,
    showInGridWhenAnyRowMatches: hasMaterialAlternatives,
    render: (row) => <SalesLineMaterialAlternativesCell row={row} suppressRowCss onChanged={onLinesChanged} />,
  };

  const normalizedColumns = [
    ...(baseColumns || []),
    materialAlternativesColumn,
  ].map((column) => ({
    ...column,
    showInGrid: AX_SALESLINE_GRID_FIELDS.has(column.key),
    ...(resolveLineGridWidth(column?.key) || {}),
    ...(String(column?.key || "").toLowerCase() === "itemid"
      ? {
          lookupDisplayFieldLabels: {
            itemId: "items.itemId",
            itemname: "items.itemname",
            inventtypeitemid: "items.inventtypeitemid",
            inventvenditemid: "items.inventvenditemid",
            inventgramajeitemid: "items.inventgramajeitemid",
            width: "items.width",
            depth: "items.depth",
          },
          lookupColumnMaxWidthByField: {
            itemname: 300,
            inventtypeitemid: 150,
            inventvenditemid: 120,
            inventgramajeitemid: 100,
          },
          disabled: (isEditing, formData) => isEditing && isPlaceholderItemId(formData?.itemId),
        }
      : {}),
    ...(resolveQtyExclusionDisabled(column?.key)
      ? { disabled: resolveQtyExclusionDisabled(column.key) }
      : {}),
  }));

  return normalizedColumns.sort((left, right) => {
    const leftOrder = AX_SALESLINE_GRID_ORDER_INDEX[left.key];
    const rightOrder = AX_SALESLINE_GRID_ORDER_INDEX[right.key];
    const safeLeftOrder = Number.isInteger(leftOrder) ? leftOrder : Number.MAX_SAFE_INTEGER;
    const safeRightOrder = Number.isInteger(rightOrder) ? rightOrder : Number.MAX_SAFE_INTEGER;
    return safeLeftOrder - safeRightOrder;
  });
};

const resolveDocumentLineGridRowStyle = (row) => {
  // Fuente de verdad única: table-config.yml (tables.salesline.frontend.ui.grid_row_styles),
  // generado en SalesOrderDocumentPageGen.js como resolveLineGridRowStyle. Cualquier regla
  // nueva/cambiada en el YAML aparece aquí automáticamente tras regenerar - ya no hay una copia
  // manual duplicada de esas reglas en este wrapper.
  const generatedDecoration = resolveLineGridRowStyle ? resolveLineGridRowStyle(row) : null;
  if (generatedDecoration) {
    return generatedDecoration;
  }
  if (hasMaterialAlternatives(row)) {
    return MATERIAL_ALTERNATIVE_ROW_DECORATION;
  }
  return null;
};

const transformDocumentHeaderColumns = (baseColumns) => {
  return (baseColumns || []).map((column) => ({
    ...column,
    showInGrid: AX_SALESTABLE_GRID_FIELDS.has(String(column?.key || "").toLowerCase()),
    ...((String(column?.key || "").toLowerCase() === "custaccount" ||
      String(column?.label || "").toLowerCase() === "salesorders.customer")
      ? {
          lookupFixedFilters: { inactive: 0 },
          lookupEnumFields: { blocked: "CustVendorBlocked" },
          lookupDisplayFieldLabels: {
            blocked: "customers.blocked",
            accountNum: "customers.accountNum",
            name: "customers.name",
            namealias: "customers.namealias",
            phone: "customers.phone",
          },
        }
      : {}),
  }));
};

const transformDocumentLineFormTabs = (baseTabs) => {
  if (!Array.isArray(baseTabs)) {
    return baseTabs;
  }

  const promoteToOverview = ["itemId", "name"];
  const promoteSet = new Set(promoteToOverview);

  return baseTabs.map((tab) => {
    if (!tab) {
      return tab;
    }

    // Remove promoted fields from every group/tab first to avoid duplicates.
    const sanitizedGroups = Array.isArray(tab.groups)
      ? tab.groups
          .map((group) => ({
            ...group,
            fields: Array.isArray(group?.fields)
              ? group.fields.filter((fieldKey) => !promoteSet.has(fieldKey))
              : group?.fields,
          }))
          .filter((group) => !Array.isArray(group?.fields) || group.fields.length > 0)
      : [];

    const sanitizedTabFields = Array.isArray(tab.fields)
      ? tab.fields.filter((fieldKey) => !promoteSet.has(fieldKey))
      : tab.fields;

    // Ensure Articulo + Texto are first in "Posicion inicial" (TabLineOverview_MainFields).
    if (tab.tabId === "TabLineOverview") {
      const groups = [...sanitizedGroups];
      const targetGroupIndex = groups.findIndex(
        (group) => group?.groupId === "TabLineOverview_MainFields",
      );
      if (targetGroupIndex >= 0) {
        const targetGroup = groups[targetGroupIndex];
        const existing = Array.isArray(targetGroup.fields) ? targetGroup.fields : [];
        const merged = [...promoteToOverview, ...existing.filter((f) => !promoteSet.has(f))];
        groups[targetGroupIndex] = { ...targetGroup, fields: merged };
      }
      return {
        ...tab,
        groups,
        fields: sanitizedTabFields,
      };
    }

    return {
      ...tab,
      groups: sanitizedGroups,
      fields: sanitizedTabFields,
    };
  });
};

export default function SalesOrderDocumentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { dataAreaId, id } = useParams();
  // Lets the Clientes screen deep-link here already filtered by customer
  // (see CustomersPage.js "view sales orders" row action) - same
  // state+query convention as the "salesOrders.viewOrderActions" navigate
  // call below, so the filter also survives a page refresh/shared link.
  const custaccountFilter = useMemo(() => {
    const fromState = String(location?.state?.initialFilters?.custaccount || "").trim();
    if (fromState) {
      return fromState;
    }
    const query = new URLSearchParams(location?.search || "");
    return (query.get("custaccount") || "").trim();
  }, [location?.state, location?.search]);
  const [downloadingSource, setDownloadingSource] = useState("");
  const [requestingDirectionReport, setRequestingDirectionReport] = useState(false);
  const [directionReportCoolingDown, setDirectionReportCoolingDown] = useState(false);
  const directionReportCooldownTimerRef = useRef(null);
  const [requestingConfirmReport, setRequestingConfirmReport] = useState(false);
  const [confirmReportCoolingDown, setConfirmReportCoolingDown] = useState(false);
  const confirmReportCooldownTimerRef = useRef(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [lineRefreshNonce, setLineRefreshNonce] = useState(0);
  const [pendingActionsCount, setPendingActionsCount] = useState(0);
  const [loadingPendingActions, setLoadingPendingActions] = useState(false);
  const [historyScope, setHistoryScope] = useState("lastYear");
  const [creatingItemKey, setCreatingItemKey] = useState("");
  const [itemSearchOpen, setItemSearchOpen] = useState(false);
  const [sourceFilesAvailability, setSourceFilesAvailability] = useState({
    pdf: false,
    eml: false,
    json: false,
    direction: false,
    confirm: false,
  });
  const [seleccionBobinasStatus, setSeleccionBobinasStatus] = useState(null);
  const [sendingToManagement, setSendingToManagement] = useState(false);
  const axRefreshTimerRef = useRef(null);
  const axRefreshBurstStartedAtRef = useRef(0);
  const axWindowSignaturesRef = useRef(new Set());
  const axAppliedRecentKeysRef = useRef(new Map());
  const skipNextAxAppliedRefreshRef = useRef(false);
  const blockedStatusByCustomerRef = useRef(new Map());
  // Snapshot of salesQty/salesqtyaux/salesunitaux taken when the line edit/
  // create form opens (see lineOnFormOpen below) - salesQty and the aux pair
  // (salesqtyaux + salesunitaux) are mutually exclusive (AX recalculates one
  // side from the other via UnitConvert), so we only lock+clear the sibling
  // once one side actually deviates from its own value at open time, not
  // just because both already carry values (existing lines normally have
  // both populated by AX).
  const lineQtySnapshotRef = useRef({ salesQty: "", salesqtyaux: "", salesunitaux: "" });
  // Which "side" is currently driving this edit session - "qty" (salesQty),
  // "aux" (salesqtyaux and/or salesunitaux), or null while neither has been
  // touched. Changing the aux UNIT alone (without retyping the aux quantity)
  // still means AX must recompute salesQty from the (now differently-scaled)
  // aux quantity, so it locks salesQty exactly like editing salesqtyaux does.
  // Deciding disabled purely from "does the other side differ from its own
  // snapshot" would deadlock: once salesQty is nulled by the patch below, it
  // differs from ITS OWN snapshot too, which would in turn disable
  // salesqtyaux (the very field the user is typing into). Tracking the
  // driving side explicitly avoids that.
  const lineQtyDrivingFieldRef = useRef(null);

  const getCustomerBlockedStatus = useCallback(async (accountNum) => {
    const normalizedAccount = String(accountNum || "").trim();
    if (!normalizedAccount) {
      return null;
    }

    if (blockedStatusByCustomerRef.current.has(normalizedAccount)) {
      return blockedStatusByCustomerRef.current.get(normalizedAccount);
    }

    // findExactByAccountNum resolves the exact account (not a "contains"
    // match), otherwise an unrelated account like "4199" could wrongly
    // block this order when checking "199".
    const customer = await customersService.findExactByAccountNum(normalizedAccount, dataAreaId);
    const blockedRaw =
      customer?.blocked ?? customer?.id?.blocked ?? customer?.Blocked ?? customer?.BLOCKED;
    const blockedInfo = {
      accountNum: normalizedAccount,
      blockedValue: normalizeBlockedValue(blockedRaw),
    };
    blockedStatusByCustomerRef.current.set(normalizedAccount, blockedInfo);
    return blockedInfo;
  }, [dataAreaId]);

  const buildBlockedCustomerMessage = useCallback((blockedCustomers) => {
    if (!Array.isArray(blockedCustomers) || blockedCustomers.length === 0) {
      return "";
    }
    const uniqueAccounts = [...new Set(blockedCustomers.map((item) => item.accountNum).filter(Boolean))];
    return `El cliente ${uniqueAccounts.join(", ")} está bloqueado (factura o todo). No se puede crear/actualizar el pedido.`;
  }, []);

  const validateBlockedCustomers = useCallback(async (formData) => {
    const accountsToValidate = [
      String(formData?.custaccount || "").trim(),
      String(formData?.invoiceaccount || "").trim(),
    ].filter(Boolean);

    if (accountsToValidate.length === 0) {
      return [];
    }

    const statuses = await Promise.all(accountsToValidate.map(getCustomerBlockedStatus));
    return statuses.filter(
      (status) => status && isCustomerBlockedForSalesOrder(status.blockedValue),
    );
  }, [getCustomerBlockedStatus]);

  const headerOnFormDataChange = useCallback(async (formData) => {
    const blockedCustomers = await validateBlockedCustomers(formData);
    if (blockedCustomers.length > 0) {
      return { modalError: buildBlockedCustomerMessage(blockedCustomers) };
    }
    return { modalError: "" };
  }, [buildBlockedCustomerMessage, validateBlockedCustomers]);

  const headerValidateForm = useCallback(async (formData) => {
    if (!formData?.[CUSTOMER_ACCOUNT_FIELD]) {
      return {
        [CUSTOMER_ACCOUNT_FIELD]: "El cliente es obligatorio.",
      };
    }

    if (!formData?.[REQUESTED_DELIV_DATE_FIELD]) {
      return {
        [REQUESTED_DELIV_DATE_FIELD]: "RequestedDelivDate es obligatorio.",
      };
    }

    const blockedCustomers = await validateBlockedCustomers(formData);
    if (blockedCustomers.length > 0) {
      return buildBlockedCustomerMessage(blockedCustomers);
    }

    return null;
  }, [buildBlockedCustomerMessage, validateBlockedCustomers]);

  const scopedSalesOrdersService = useMemo(
    () => ({
      ...salesOrdersService,
      getAll: async (page = 0, pageSize = 100, sort = [], filters = {}) => {
        const hasExplicitSort = Array.isArray(sort) && sort.length > 0;
        const effectiveSort = hasExplicitSort ? sort : [{ field: "recId", direction: "desc" }];
        const effectiveFilters = { ...(filters || {}) };
        if (id) {
          effectiveFilters.iSalesId = id;
          if (dataAreaId) {
            effectiveFilters.dataAreaId = dataAreaId;
          }
        }
        if (!id && historyScope === "lastYear" && !effectiveFilters.modifieddateFrom) {
          const lastYear = new Date();
          lastYear.setFullYear(lastYear.getFullYear() - 1);
          effectiveFilters.modifieddateFrom = lastYear.toISOString();
        }
        return salesOrdersService.getAll(page, pageSize, effectiveSort, effectiveFilters);
      },
      create: async (payload = {}) => {
        const response = await salesOrdersService.create(payload);
        let identity = resolveCreatedSalesOrderIdentity(response, payload);

        if (!identity?.iSalesId) {
          const fallbackFilters = {};
          if (dataAreaId) {
            fallbackFilters.dataAreaId = dataAreaId;
          }
          const custAccount = getFieldValueLoose(payload, "custaccount");
          if (custAccount) {
            fallbackFilters.custaccount = custAccount;
          }

          const fallbackResult = await salesOrdersService.getAll(
            0,
            1,
            [{ field: "recId", direction: "desc" }],
            fallbackFilters,
          );
          const fallbackRow = Array.isArray(fallbackResult?.content)
            ? fallbackResult.content[0]
            : null;
          identity = resolveSalesOrderIdentityFromRow(fallbackRow);
        }

        if (identity?.iSalesId && identity.iSalesId !== id) {
          navigate(buildSalesOrderDetailRoute(identity), { replace: true });
        }
        return response;
      },
      update: async (recordId, payload = {}) => {
        const response = await salesOrdersService.update(recordId, payload);

        const identityFromResponse = resolveCreatedSalesOrderIdentity(response, payload);
        const identityFromIdObject =
          recordId && typeof recordId === "object" && !Array.isArray(recordId)
            ? resolveSalesOrderIdentityFromRow({ id: recordId })
            : null;
        const identityFromPayload = resolveCreatedSalesOrderIdentity(null, payload);
        const effectiveIdentity =
          identityFromResponse ||
          identityFromIdObject ||
          identityFromPayload ||
          (id ? { iSalesId: String(id), dataAreaId: dataAreaId ? String(dataAreaId) : "" } : null);

        if (effectiveIdentity?.iSalesId) {
          const targetRoute = buildSalesOrderDetailRoute(effectiveIdentity);
          const currentRoute = buildSalesOrderDetailRoute({
            iSalesId: String(id || ""),
            dataAreaId: dataAreaId ? String(dataAreaId) : "",
          });

          if (targetRoute !== currentRoute) {
            navigate(targetRoute, { replace: true });
          }
        }

        return response;
      },
    }),
    [dataAreaId, historyScope, id, navigate],
  );

  const lineOnFormOpen = useCallback((data) => {
    lineQtySnapshotRef.current = {
      salesQty: data?.salesQty ?? "",
      salesqtyaux: data?.salesqtyaux ?? "",
      salesunitaux: data?.salesunitaux ?? "",
    };
    lineQtyDrivingFieldRef.current = null;
  }, []);

  const lineOnFormDataChange = useCallback((nextFormData) => {
    const snapshot = lineQtySnapshotRef.current;
    const driving = lineQtyDrivingFieldRef.current;

    if (!driving) {
      const qtyTouched = !isSameQtyValue(nextFormData?.salesQty, snapshot.salesQty);
      // Touching the aux UNIT alone (without retyping the aux quantity) still
      // means the aux side no longer matches what AX last computed for
      // salesQty, so it must lock+clear salesQty exactly like editing
      // salesqtyaux does.
      const auxTouched =
        !isSameQtyValue(nextFormData?.salesqtyaux, snapshot.salesqtyaux) ||
        !isSameQtyValue(nextFormData?.salesunitaux, snapshot.salesunitaux);

      if (auxTouched && !qtyTouched) {
        lineQtyDrivingFieldRef.current = "aux";
        return { formDataPatch: { salesQty: null } };
      }
      if (qtyTouched && !auxTouched) {
        lineQtyDrivingFieldRef.current = "qty";
        return { formDataPatch: { salesqtyaux: null } };
      }
      return null;
    }

    // Already locked to one side - release the lock once that side is
    // brought back to its original (open-time) value(s), so both sides
    // become editable again.
    if (driving === "qty") {
      if (isSameQtyValue(nextFormData?.salesQty, snapshot.salesQty)) {
        lineQtyDrivingFieldRef.current = null;
      }
    } else if (
      isSameQtyValue(nextFormData?.salesqtyaux, snapshot.salesqtyaux) &&
      isSameQtyValue(nextFormData?.salesunitaux, snapshot.salesunitaux)
    ) {
      lineQtyDrivingFieldRef.current = null;
    }
    return null;
  }, []);

  const renderLineRowActions = useCallback((row) => {
    if (!isPlaceholderItemId(getFieldValueLoose(row, "itemId"))) return null;
    const key = JSON.stringify(row?.id || row);
    const creating = creatingItemKey === key;
    return (
      <FontAwesomeIcon icon={faPlus} className="action-icon action-edit"
        title={t("salesLines.materialAlternatives.createItem")}
        aria-label={t("salesLines.materialAlternatives.createItem")}
        aria-disabled={creating}
        style={creating ? { opacity: 0.45, cursor: "wait" } : undefined}
        onClick={async () => {
          if (creating) return;
          setCreatingItemKey(key);
          try {
            await salesLineActionsService.createPlaceholderItem(row.id || row);
            window.alert(t("salesLines.materialAlternatives.createQueued"));
          } catch (error) {
            console.error("Error queueing item creation:", error);
            window.alert(t("salesLines.materialAlternatives.createError"));
          } finally {
            setCreatingItemKey("");
          }
        }} />
    );
  }, [creatingItemKey, t]);

  const flushScheduledRefresh = useCallback(() => {
    if (axRefreshTimerRef.current) {
      clearTimeout(axRefreshTimerRef.current);
      axRefreshTimerRef.current = null;
    }
    axRefreshBurstStartedAtRef.current = 0;
    axWindowSignaturesRef.current.clear();
    setRefreshNonce((current) => current + 1);
  }, []);

  const clearScheduledRefresh = useCallback(() => {
    if (axRefreshTimerRef.current) {
      clearTimeout(axRefreshTimerRef.current);
      axRefreshTimerRef.current = null;
    }
    axRefreshBurstStartedAtRef.current = 0;
    axWindowSignaturesRef.current.clear();
  }, []);

  useEffect(() => {
    setRefreshNonce(0);
    clearScheduledRefresh();
    axAppliedRecentKeysRef.current.clear();
    skipNextAxAppliedRefreshRef.current = false;
  }, [clearScheduledRefresh, dataAreaId, id]);

  useEffect(() => {
    let cancelled = false;

    const emptyAvailability = { pdf: false, eml: false, json: false, direction: false, confirm: false };
    if (!dataAreaId || !id) {
      setSourceFilesAvailability(emptyAvailability);
      return () => {
        cancelled = true;
      };
    }

    const loadSourceFilesAvailability = async () => {
      try {
        const availability = await salesOrdersService.getSourceFilesAvailability(dataAreaId, id);
        if (!cancelled) {
          setSourceFilesAvailability({
            pdf: Boolean(availability?.pdf),
            eml: Boolean(availability?.eml),
            json: Boolean(availability?.json),
            direction: Boolean(availability?.direction),
            confirm: Boolean(availability?.confirm),
          });
        }
      } catch (error) {
        if (!cancelled) {
          setSourceFilesAvailability(emptyAvailability);
        }
      }
    };

    loadSourceFilesAvailability();

    return () => {
      cancelled = true;
    };
  }, [dataAreaId, id, refreshNonce]);

  useEffect(() => {
    let cancelled = false;
    if (!id) {
      setSeleccionBobinasStatus(null);
      return () => { cancelled = true; };
    }
    const loadSeleccionBobinasStatus = async () => {
      try {
        const filters = { iSalesId: id };
        if (dataAreaId) filters.dataAreaId = dataAreaId;
        const result = await salesOrdersService.getAll(0, 1, [], filters);
        const row = Array.isArray(result?.content) ? result.content[0] : null;
        if (!cancelled) {
          setSeleccionBobinasStatus(row ? {
            // null (not 0) means this order has not been synced with this
            // field from AX4 yet - keep that distinction for rendering.
            estado: toNumberOrNull(getFieldValueLoose(row, "cmtseleccionbobinasestadopv")),
            enviadoPor: getFieldValueLoose(row, "cmtseleccionbobinasenviadopor") || "",
          } : null);
        }
      } catch (error) {
        if (!cancelled) setSeleccionBobinasStatus(null);
      }
    };
    loadSeleccionBobinasStatus();
    return () => { cancelled = true; };
  }, [dataAreaId, id, refreshNonce]);

  const handleSendToManagement = useCallback(async () => {
    if (!id || sendingToManagement) return;
    setSendingToManagement(true);
    try {
      const result = await cmtSeleccionBobinasService.sendToManagement(id);
      window.alert(t("seleccionBobinas.actions.sendSuccess"));
      setSeleccionBobinasStatus({
        estado: Number(result?.cmtSeleccionBobinasEstadoPV) || 0,
        enviadoPor: result?.cmtSeleccionBobinasEnviadoPor || "",
      });
    } catch (error) {
      window.alert(error?.message || t("seleccionBobinas.actions.sendError"));
    } finally {
      setSendingToManagement(false);
    }
  }, [id, sendingToManagement, t]);

  const openSelectorBobinas = useCallback(() => {
    if (!id) return;
    const path = dataAreaId
      ? `/legacy/selector-bobinas/${encodeURIComponent(dataAreaId)}/${encodeURIComponent(id)}`
      : `/legacy/selector-bobinas/document/${encodeURIComponent(id)}`;
    navigate(path);
  }, [dataAreaId, id, navigate]);

  useEffect(() => () => {
    clearScheduledRefresh();
    axAppliedRecentKeysRef.current.clear();
    skipNextAxAppliedRefreshRef.current = false;
  }, [clearScheduledRefresh]);

  useEffect(() => {
    let cancelled = false;

    const loadPendingActions = async () => {
      if (!dataAreaId || !id) {
        if (!cancelled) {
          setPendingActionsCount(0);
        }
        return;
      }

      setLoadingPendingActions(true);
      try {
        const result = await salesOrderInboundMessagesService.getAll(0, 200, [], {
          dataAreaId,
          iSalesId: id,
        });
        const rows = Array.isArray(result?.content) ? result.content : [];
        const pending = rows.filter((row) =>
          PENDING_ACTION_STATUSES.has(String(row?.processingstatus || "").trim().toLowerCase()),
        ).length;
        if (!cancelled) {
          setPendingActionsCount(pending);
        }
      } catch (error) {
        if (!cancelled) {
          setPendingActionsCount(0);
        }
      } finally {
        if (!cancelled) {
          setLoadingPendingActions(false);
        }
      }
    };

    loadPendingActions();
    return () => {
      cancelled = true;
    };
  }, [dataAreaId, id, refreshNonce]);

  const handleReverseSyncExecutionEvent = useCallback((eventData) => {
    const eventName = eventData?.eventName || "";
    const eventType = eventData?.type || "REVERSE_SYNC_EXECUTION";
    const payload = eventData?.payload || null;

    if (eventType === "REVERSE_SYNC_EXECUTION") {
      const functionalFailed = String(payload?.functionalStatus || "").toUpperCase() === "FAILED";
      const technicalFailed = String(payload?.technicalStatus || "").toUpperCase() === "FAILED";
      if (functionalFailed || technicalFailed) {
        // When reverse-sync ends in failure, AX usually publishes follow-up changes.
        // Skip the first AX4->IDAX applied refresh to avoid double refreshes.
        skipNextAxAppliedRefreshRef.current = true;
      }
      return;
    }

    // Do not refresh the whole screen on reverse-sync feedback events.
    // We refresh only from AX4 -> IDAX applied events to avoid duplicate refreshes.
    if (eventType !== "AX4_TO_IDAX_SYNC_APPLIED" || eventName !== "ax4-idax-sync-applied") {
      return;
    }

    if (skipNextAxAppliedRefreshRef.current) {
      skipNextAxAppliedRefreshRef.current = false;
      return;
    }

    const now = Date.now();
    const dedupKey = [
      payload?.entity || "",
      payload?.operation || "",
      payload?.businessKey || "",
      payload?.dataAreaId || "",
    ].join("|");
    if (dedupKey !== "|||") {
      const lastSeenAt = axAppliedRecentKeysRef.current.get(dedupKey) || 0;
      if (now - lastSeenAt < AX_APPLIED_DUPLICATE_WINDOW_MS) {
        return;
      }
      axAppliedRecentKeysRef.current.set(dedupKey, now);
      // Prevent unbounded growth of dedup keys map.
      for (const [key, ts] of axAppliedRecentKeysRef.current.entries()) {
        if (now - ts > AX_APPLIED_DUPLICATE_WINDOW_MS * 4) {
          axAppliedRecentKeysRef.current.delete(key);
        }
      }
    }

    const signature = [
      eventType,
      payload?.messageId || "",
      payload?.entity || "",
      payload?.operation || "",
      payload?.processedAt || payload?.updatedAt || payload?.occurredAt || "",
    ].join("|");

    if (axWindowSignaturesRef.current.has(signature)) {
      return;
    }
    axWindowSignaturesRef.current.add(signature);

    if (!axRefreshBurstStartedAtRef.current) {
      axRefreshBurstStartedAtRef.current = now;
    }

    const elapsed = now - axRefreshBurstStartedAtRef.current;
    if (elapsed >= AX_REFRESH_MAX_WAIT_MS) {
      flushScheduledRefresh();
      return;
    }

    const remainingUntilMaxWait = AX_REFRESH_MAX_WAIT_MS - elapsed;
    const nextDelay = Math.min(AX_REFRESH_DEBOUNCE_MS, remainingUntilMaxWait);
    if (axRefreshTimerRef.current) {
      clearTimeout(axRefreshTimerRef.current);
    }
    axRefreshTimerRef.current = setTimeout(() => {
      flushScheduledRefresh();
    }, nextDelay);
  }, [flushScheduledRefresh]);

  const downloadBlob = useCallback(async (kind) => {
    if (!dataAreaId || !id || downloadingSource) {
      return;
    }
    setDownloadingSource(kind);
    try {
      let payload;
      if (kind === "pdf") {
        payload = await salesOrdersService.downloadSourcePdf(dataAreaId, id);
      } else if (kind === "eml") {
        payload = await salesOrdersService.downloadSourceEmail(dataAreaId, id);
      } else if (kind === "direction") {
        payload = await salesOrdersService.downloadDirectionReport(dataAreaId, id);
      } else if (kind === "confirm") {
        payload = await salesOrdersService.downloadConfirmReport(dataAreaId, id);
      } else {
        payload = await salesOrdersService.downloadSourceJson(dataAreaId, id);
      }
      const url = window.URL.createObjectURL(payload.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = payload.filename || `${id}.${kind}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Error downloading source ${kind}:`, error);
      window.alert("No se pudo descargar el archivo origen.");
    } finally {
      setDownloadingSource("");
    }
  }, [dataAreaId, downloadingSource, id]);

  const handleRequestDirectionReport = useCallback(async () => {
    if (!dataAreaId || !id || requestingDirectionReport || directionReportCoolingDown) return;
    setRequestingDirectionReport(true);
    try {
      await salesOrdersService.requestDirectionReport(dataAreaId, id);
      window.alert(t("salesOrders.directionReportRequestSuccess"));
      // The file is produced asynchronously by AX4/the reverse-sync worker,
      // so this refresh is best-effort - the availability flag may still be
      // false right after this call. The SSE subscription below (reverse-sync
      // execution stream) will refresh it automatically once AX4 actually
      // processes the request.
      setRefreshNonce((current) => current + 1);
    } catch (error) {
      window.alert(error?.message || t("salesOrders.directionReportRequestError"));
    } finally {
      setRequestingDirectionReport(false);
      // Cooldown: prevent spamming the reverse-sync outbox with duplicate
      // requests while the previous one is still being processed by AX4.
      setDirectionReportCoolingDown(true);
      if (directionReportCooldownTimerRef.current) {
        clearTimeout(directionReportCooldownTimerRef.current);
      }
      directionReportCooldownTimerRef.current = setTimeout(() => {
        setDirectionReportCoolingDown(false);
        directionReportCooldownTimerRef.current = null;
      }, DIRECTION_REPORT_COOLDOWN_MS);
    }
  }, [dataAreaId, id, requestingDirectionReport, directionReportCoolingDown, t]);

  useEffect(() => () => {
    if (directionReportCooldownTimerRef.current) {
      clearTimeout(directionReportCooldownTimerRef.current);
    }
  }, []);

  const handleRequestConfirmReport = useCallback(async () => {
    if (!dataAreaId || !id || requestingConfirmReport || confirmReportCoolingDown) return;
    setRequestingConfirmReport(true);
    try {
      await salesOrdersService.requestConfirmReport(dataAreaId, id);
      window.alert(t("salesOrders.confirmReportRequestSuccess"));
      // The confirmation and its PDF are produced asynchronously by AX4/the
      // reverse-sync worker; the SSE subscription below refreshes availability
      // once AX4 actually processes the request.
      setRefreshNonce((current) => current + 1);
    } catch (error) {
      window.alert(error?.message || t("salesOrders.confirmReportRequestError"));
    } finally {
      setRequestingConfirmReport(false);
      // Cooldown: prevent spamming the reverse-sync outbox with duplicate
      // requests while the previous one is still being processed by AX4.
      setConfirmReportCoolingDown(true);
      if (confirmReportCooldownTimerRef.current) {
        clearTimeout(confirmReportCooldownTimerRef.current);
      }
      confirmReportCooldownTimerRef.current = setTimeout(() => {
        setConfirmReportCoolingDown(false);
        confirmReportCooldownTimerRef.current = null;
      }, CONFIRM_REPORT_COOLDOWN_MS);
    }
  }, [dataAreaId, id, requestingConfirmReport, confirmReportCoolingDown, t]);

  useEffect(() => () => {
    if (confirmReportCooldownTimerRef.current) {
      clearTimeout(confirmReportCooldownTimerRef.current);
    }
  }, []);

  // Auto-refresh source-files availability when AX4 finishes processing the
  // "generate direction report" business action. This piggybacks on the same
  // single SSE connection ReverseSyncStatusPanel already opens for SalesTable/
  // SalesLine/InventItemCreation (via extraStreamEntities), instead of opening
  // a 4th dedicated connection - each open sales-order tab used to hold 4
  // concurrent long-lived connections, which was enough to exhaust the
  // browser's per-origin connection limit with just 2 tabs open and made the
  // app appear to hang.
  const handleDirectionReportStreamMessage = useCallback((entity, entry) => {
    if (entity !== "SalesTableDirectionReport" && entity !== "SalesTableConfirmReport") {
      return;
    }
    const succeeded = entry?.technicalStatus === "SUCCEEDED" && entry?.functionalStatus !== "FAILED";
    if (succeeded) {
      setRefreshNonce((current) => current + 1);
    }
  }, []);

  return (
    <>
      <ReverseSyncStatusPanel
        dataAreaId={dataAreaId}
        salesOrderId={id}
        onExecutionEvent={handleReverseSyncExecutionEvent}
        extraStreamEntities={DIRECTION_REPORT_STREAM_ENTITIES}
        onExtraEntityMessage={handleDirectionReportStreamMessage}
      />
      {!!id && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <SourceFilesMenu
          availability={{
            pdf: Boolean(dataAreaId && sourceFilesAvailability.pdf),
            eml: Boolean(dataAreaId && sourceFilesAvailability.eml),
            json: Boolean(dataAreaId && sourceFilesAvailability.json),
          }}
          loadingKind={["pdf", "eml", "json"].includes(downloadingSource) ? downloadingSource : ""}
          onSelect={downloadBlob}
          buttonClassName="btn-primary btn-sm"
        />
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={
            !dataAreaId
            || !id
            || !sourceFilesAvailability.direction
            || Boolean(downloadingSource)
          }
          onClick={() => downloadBlob("direction")}
        >
          <i className="fa fa-file-pdf-o" aria-hidden="true" /> {t("salesOrders.directionReport")}
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          title={directionReportCoolingDown
            ? t("salesOrders.directionReportRequestCooldownHint")
            : t("salesOrders.directionReportRequestHint")}
          disabled={!dataAreaId || !id || requestingDirectionReport || directionReportCoolingDown}
          onClick={handleRequestDirectionReport}
        >
          <i className={`fa ${requestingDirectionReport ? "fa-spinner fa-spin" : "fa-refresh"}`} aria-hidden="true" /> {t("salesOrders.directionReportRequest")}
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={
            !dataAreaId
            || !id
            || !sourceFilesAvailability.confirm
            || Boolean(downloadingSource)
          }
          onClick={() => downloadBlob("confirm")}
        >
          <i className="fa fa-file-pdf-o" aria-hidden="true" /> {t("salesOrders.confirmReport")}
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          title={confirmReportCoolingDown
            ? t("salesOrders.confirmReportRequestCooldownHint")
            : t("salesOrders.confirmReportRequestHint")}
          disabled={!dataAreaId || !id || requestingConfirmReport || confirmReportCoolingDown}
          onClick={handleRequestConfirmReport}
        >
          <i className={`fa ${requestingConfirmReport ? "fa-spinner fa-spin" : "fa-refresh"}`} aria-hidden="true" /> {t("salesOrders.confirmReportRequest")}
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!dataAreaId || !id}
          onClick={() => {
            const query = new URLSearchParams({
              dataAreaId: String(dataAreaId || ""),
              iSalesId: String(id || ""),
            }).toString();
            navigate(`/legacy/sales-order-inbound-messages?${query}`, {
              state: { initialFilters: { dataAreaId, iSalesId: id } },
            });
          }}
        >
          {t("salesOrders.viewOrderActions")}
        </button>
        {seleccionBobinasStatus && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "2px 10px",
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 600,
              ...(seleccionBobinasStatus.estado === null
                ? NOT_SYNCED_BADGE_STYLE
                : estadoPvBadgeStyle(seleccionBobinasStatus.estado)),
            }}
            title={seleccionBobinasStatus.estado === null
              ? t("seleccionBobinas.notSyncedHint")
              : (seleccionBobinasStatus.enviadoPor
                ? t("seleccionBobinas.integration.sentByTitle", { user: seleccionBobinasStatus.enviadoPor })
                : undefined)}
          >
            {seleccionBobinasStatus.estado === null
              ? NOT_SYNCED_LABEL
              : t(SELECCION_BOBINAS_ESTADO_LABEL_KEYS[seleccionBobinasStatus.estado] || SELECCION_BOBINAS_ESTADO_LABEL_KEYS[0])}
          </span>
        )}
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={!dataAreaId && !id}
          onClick={openSelectorBobinas}
        >
          {t("seleccionBobinas.integration.openSelector")}
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!id || sendingToManagement}
          onClick={handleSendToManagement}
        >
          {t("seleccionBobinas.integration.sendToManagement")}
        </button>
        </div>
      )}
      {!!id && (
        <div
          style={{
            marginBottom: 12,
            padding: "8px 12px",
            borderRadius: 6,
            border: "1px solid #ffe0a3",
            backgroundColor: "#fff8eb",
            color: "#7a4b00",
            fontSize: 13,
          }}
        >
          {loadingPendingActions
            ? t("salesOrders.pendingActionsLoading")
            : pendingActionsCount > 0
              ? t("salesOrders.pendingActionsCount", { count: pendingActionsCount })
              : t("salesOrders.noPendingActions")}
        </div>
      )}
      {!id && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <label htmlFor="sales-order-history-scope" style={{ margin: 0, fontWeight: 600 }}>
            {t("documentPage.historyScopeLabel")}
          </label>
          <select
            id="sales-order-history-scope"
            className="form-control"
            style={{ width: 240 }}
            value={historyScope}
            onChange={(event) => setHistoryScope(event.target.value)}
          >
            <option value="lastYear">{t("documentPage.historyScopeLastYear")}</option>
            <option value="all">{t("documentPage.historyScopeAll")}</option>
          </select>
        </div>
      )}
      <SalesOrderDocumentPageGen
        key={`sales-doc:${dataAreaId || ""}:${id || ""}:${historyScope}:${custaccountFilter}:${refreshNonce}`}
        headerServiceOverride={scopedSalesOrdersService}
        transformHeaderColumns={transformDocumentHeaderColumns}
        headerValidateForm={headerValidateForm}
        headerOnFormDataChange={headerOnFormDataChange}
        headerCrudConfigOverride={{
          modalClassName: "modal-xwide",
          initialFilters: custaccountFilter ? { custaccount: custaccountFilter } : {},
          renderForm: ({ formData, onChange, renderDefaultForm }) => (
            <>
              <DeliveryAddressPickerButton
                custAccount={formData.custaccount}
                dataAreaId={dataAreaId}
                onSelect={(addressFields) =>
                  onChange({ ...formData, ...addressFields })
                }
              />
              {renderDefaultForm()}
            </>
          ),
        }}
        lineCrudConfigOverride={{
          modalClassName: "modal-xwide",
          gridRowStyleResolver: resolveDocumentLineGridRowStyle,
          renderRowActions: renderLineRowActions,
          renderCreateActions: (
            <button type="button" className="btn btn-primary" style={{ marginRight: 8 }} onClick={() => setItemSearchOpen(true)}>
              {t("salesLines.itemSearch.open")}
            </button>
          ),
          onFormOpen: lineOnFormOpen,
          onFormDataChange: lineOnFormDataChange,
        }}
        lineRefreshToken={lineRefreshNonce}
        transformLineColumns={(columns) => transformDocumentLineColumns(
          columns,
          () => setLineRefreshNonce((current) => current + 1),
          lineQtyDrivingFieldRef,
        )}
        transformLineFormTabs={transformDocumentLineFormTabs}
      />
      {itemSearchOpen ? <SalesLineItemSearchModal dataAreaId={dataAreaId} iSalesId={id}
        onClose={() => setItemSearchOpen(false)}
        onQueued={() => { setItemSearchOpen(false); window.alert(t("salesLines.materialAlternatives.createQueued")); }} /> : null}
    </>
  );
}


